import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import RouteConstant from "../../constants/route.constants";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { Divider } from "primereact/divider";
import { ProductCheckoutModel } from "../../models/products/product-checkout-model";
import {
  calculateItemSum,
  calculateItemTotal,
  calculateTotalTax,
} from "./product-calculation";
import Helpers from "../../utils/helpers";
import "./products-checkout-dialog.css";

const ProductsCheckoutDialog = ({
  productData,
  showCheckoutDialog,
  hideCheckoutDialog,
  proceedToCheckout,
}: any) => {
  const [productDialog, setProductDialog] =
    useState<ProductCheckoutModel>(productData);
  const navigate = useNavigate();
  const [deliveryCharge] = useState(0);

  useEffect(() => {
    setProductDialog(productData);
  }, [productData]);

  // Custom header with success indicator
  const header = (
    <div className="flex align-items-center gap-3">
      <div className="flex align-items-center justify-content-center bg-green-100 border-circle"
        style={{ width: '3rem', height: '3rem' }}>
        <i className="pi pi-check text-green-600" style={{ fontSize: '1.5rem' }}></i>
      </div>
      <div className="flex flex-column">
        <span className="text-xl font-semibold text-900">Added to Cart!</span>
        {/* <span className="text-sm text-600">Ready to checkout when you are</span> */}
      </div>
    </div>
  );

  // Custom footer with better CTA hierarchy
  const footer = (
    <div className="flex flex-column gap-2 pt-3">
      <Button
        label="Proceed to Checkout"
        icon="pi pi-shopping-cart"
        className="w-full p-button-success font-semibold"
        onClick={() => navigate(RouteConstant.basket)}
        raised
        size="large"
      />
      <Button
        label="Continue Shopping"
        icon="pi pi-arrow-left"
        className="w-full p-button-text p-button-secondary"
        onClick={hideCheckoutDialog}
      />
    </div>
  );

  const itemSubtotal = calculateItemSum(
    productDialog.unitPrice ?? 0,
    productDialog.quantity
  );
  const taxAmount = calculateTotalTax(
    productDialog.unitPrice ?? 0,
    productDialog.quantity,
    productDialog.tax
  );
  const totalAmount = calculateItemTotal(
    productDialog.unitPrice ?? 0,
    productDialog.quantity,
    taxAmount,
    deliveryCharge
  );

  return (
    <Dialog
      visible={showCheckoutDialog}
      header={header}
      footer={footer}
      className="custom-dialog checkout-prod"
      style={{ width: '90vw', maxWidth: '500px' }}
      breakpoints={{ "960px": "75vw", "641px": "95vw" }}
      modal
      onHide={hideCheckoutDialog}
    >
      {/* Product Card Section */}
      <div className="bg-gray-50 border-round-lg p-4 mb-4">
        <div className="flex gap-4 align-items-start">
          {/* Product Image */}
          <div className="flex-shrink-0">
            {productDialog.productImageList && (
              <img
                src={
                  (Array.isArray(productDialog.productImageList)
                    ? (productDialog.productImageList as any)?.mediaUrl
                    : productDialog.productImageList?.mediaUrl) ??
                  process.env.PUBLIC_URL + "/assests/images/Desiri-Groundnut-Oils.png"
                }
                alt={productDialog.name}
                className="border-round-md shadow-2"
                style={{ width: '100px', height: '100px', objectFit: 'contain' }}
              />
            )}
          </div>

          {/* Product Details */}
          <div className="flex-grow-1">
            <h3 className="text-xl font-semibold text-900 mt-0 mb-2 line-height-3">
              {productDialog.name}
            </h3>
            <div className="flex flex-column gap-2">
              <div className="flex align-items-center gap-2">
                <i className="pi pi-tag text-600" style={{ fontSize: '0.875rem' }}></i>
                <span className="text-2xl font-bold text-primary">
                  {Helpers.formatAmountInINR(productDialog.unitPrice)}
                </span>
                <span className="text-sm text-600">per unit</span>
              </div>
              <div className="flex align-items-center gap-3 text-sm">
                <div className="flex align-items-center gap-1">
                  <i className="pi pi-box text-600"></i>
                  <span className="text-700 font-medium">{productDialog.weight}</span>
                </div>
                <div className="flex align-items-center gap-1">
                  <i className="pi pi-shopping-bag text-600"></i>
                  <span className="text-700 font-medium">Qty: {productDialog.quantity}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Price Breakdown Section */}
      <div className="flex flex-column gap-3">
        <h4 className="text-lg font-semibold text-900 m-0 mb-2">Order Summary</h4>

        {/* Subtotal */}
        <div className="flex justify-content-between align-items-center">
          <span className="text-700">
            <i className="pi pi-shopping-cart mr-2 text-600"></i>
            Subtotal ({productDialog.quantity} {productDialog.quantity > 1 ? 'items' : 'item'})
          </span>
          <span className="font-semibold text-900">
            {Helpers.formatAmountInINR(itemSubtotal)}
          </span>
        </div>

        {/* Tax */}
        <div className="flex justify-content-between align-items-center">
          <span className="text-700">
            <i className="pi pi-percentage mr-2 text-600"></i>
            Tax ({productDialog.tax}%)
          </span>
          <span className="font-semibold text-900">
            {Helpers.formatAmountInINR(taxAmount)}
          </span>
        </div>

        {/* Delivery */}
        {/* <div className="flex justify-content-between align-items-center">
          <span className="text-700">
            <i className="pi pi-truck mr-2 text-600"></i>
            Delivery Charges
          </span>
          <span className="font-semibold text-success">
            {deliveryCharge === 0 ? 'FREE' : Helpers.formatAmountInINR(deliveryCharge)}
          </span>
        </div> */}

        <Divider className="my-2" />

        {/* Total */}
        <div className="flex justify-content-between align-items-center bg-primary-50 p-3 border-round-md">
          <span className="text-xl font-bold text-900">Total</span>
          <span className="text-2xl font-bold text-primary">
            {Helpers.formatAmountInINR(totalAmount)}
          </span>
        </div>
      </div>
    </Dialog>
  );
};

export default ProductsCheckoutDialog;
